import {
  EmailTemplateTypes,
  PaymentProviderType,
  accountingSystemProvider,
} from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { When } from "react-if";

import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useDisableCustomPaymentsMutation, useEnableCustomPaymentsMutation } from "../../../../services/api/organisationApi/organisation";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { TextEditor } from "../../../Molecules/TextEditor/TextEditor";
import { useMergeTags } from "../../../../hooks/useMergeTagsAndLinks";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { Button } from "../../../../../common/Atoms/Button";
import { Badge } from "../../../../../common/Atoms/Badge";

export function CustomPaymentLink() {
  const [enableCustomPayments, { isLoading: isEnableLoading }] = useEnableCustomPaymentsMutation();
  const [disableCustomPayments, { isLoading: isDisableLoading }] = useDisableCustomPaymentsMutation();
  const currentOrg = useGetSelectedOrganisation();
  const [paymentLinkTemplate, setPaymentLinkTemplate] = useState<string>(``);
  const [showCustomEditor, setShowCustomEditor] = useState<boolean>(false);
  const mergeTags = useMergeTags(EmailTemplateTypes.CUSTOM_PAYMENT_LINK);

  const formattedMergeTags = useMemo(() => {
    return mergeTags.asArray
      .map(tag => {
        return {
          text: tag.name,
          value: tag.value,
        };
      });
  }, [ mergeTags ]);

  const { currentProvider, enabled } = useMemo(() => {
    const currentProvider = currentOrg?.paymentProviders.find(p => p.type === PaymentProviderType.CUSTOM);

    return {
      enabled: !!currentProvider,
      currentProvider,
    };
  }, [ currentOrg?.paymentProviders ]);

  // Update the template string state
  useEffect(() => {
    if (
      currentProvider &&
      currentProvider.payment_link_template &&
      currentProvider.payment_link_template !== paymentLinkTemplate
    ) {
      setPaymentLinkTemplate(currentProvider.payment_link_template);
      setShowCustomEditor(true); // Show editor if there's existing content
    }

    if (!currentProvider && paymentLinkTemplate && currentOrg) {
      setPaymentLinkTemplate(``);
      setShowCustomEditor(false);
    }
  }, [currentProvider, currentOrg]);

  const formDirty = useMemo(() => {
    if (!currentProvider) {
      return false;
    }

    if (paymentLinkTemplate !== currentProvider.payment_link_template) {
      return true;
    }

    return false;
  }, [ paymentLinkTemplate, currentProvider ]);

  function handleChange() {
    if (!enabled) {
      handleEnable();
    }
    else {
      disableCustomPayments(currentOrg.id);
    }
  }

  // May be called from toggle or save changes
  function handleEnable() {
    if (!paymentLinkTemplate) {
      toast.error(`Please enter a custom payment link template before enabling.`);

      return;
    }

    enableCustomPayments({
      organisationId: currentOrg.id,
      paymentLinkTemplate,
    });
  }

  function handleContentChange({ text }: { text: string }) {
    setPaymentLinkTemplate(text);
  }

  const placeholder = accountingSystemProvider(currentOrg)?.customPaymentLinkPlaceholder;

  return (
    <TwoColFormRow
      badge={ <Badge
        color={ `purple` }
        className={ `max-w-fit max-h-fit inline-block` }
      >
        { `NEW` }
      </Badge> }
      heading={ `Custom Payment Link` }
    >
      <Toggle
        description={ `Enable payment links for other providers. Adds pay now buttons to customer portal pages and merge tag for emails.` }
        checked={ enabled }
        onChange={ handleChange }
        loading={ isEnableLoading || isDisableLoading }
        label={ `Enable Custom Payment Link` }
        className={ `mb-2` }
      />

      <When condition={ !showCustomEditor }>
        <button
          onClick={ () => setShowCustomEditor(true) }
          className={ `text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer` }
        >
          { `+ Add a custom payment link` }
        </button>
      </When>

      <When condition={ showCustomEditor }>
        <Paragraph
          variant={ `secondary` }
        >
          { `Create the custom link here and use the merge tags to insert required specific values` }
        </Paragraph>
        <TextEditor
          value={ paymentLinkTemplate }
          onChange={ handleContentChange }
          mergeTags={ formattedMergeTags }
          mode={ `sms` }
          editorClassname={ `min-h-[50px]` }
          placeholder={ placeholder }
        />
      </When>

      <When condition={ formDirty }>
        <div className={ `flex justify-end mt-2` }>
          <Button
            color={ `orange` }
            onClick={ handleEnable }
          >
            { `Save Changes` }
          </Button>
        </div>
      </When>

    </TwoColFormRow>
  );
}
